import React, { useEffect, useState } from "react";
import axios from "utils/axios";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { HiChevronDown } from "react-icons/hi2";
import Button from "components/ui/Button";
import SearchSelect from "components/ui/SearchSelect";

const PROVIDER_FILTER_OPTIONS = [
  { value: "", label: "All Providers" },
  { value: "twilio", label: "Twilio" },
  { value: "intellisms", label: "IntelliSMS" },
];

const SmsLogs = () => {
  const [smsLogs, setSmsLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({
    totalAlreadyPaidCount: 0,
    totalChargedCount: 0,
    grandTotal: 0,
    senderBreakdown: {
      alpha: { alreadyPaid: 0, charged: 0, total: 0 },
      other: { alreadyPaid: 0, charged: 0, total: 0 },
    },
    purposeBreakdown: {},
  });
  const [dateRangeLabel, setDateRangeLabel] = useState("All Time");

  // Filter states
  const [selectedFilter, setSelectedFilter] = useState("");
  const [customDateRange, setCustomDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [senderFilter, setSenderFilter] = useState("");
  const [purposeFilter, setPurposeFilter] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [availablePurposes, setAvailablePurposes] = useState([]);

  // Convert frontend filter values to backend API parameters
  const getApiTimeFrame = (filterValue) => {
    switch (filterValue) {
      case "today":
        return "today";
      case "last-week":
        return "lastWeek";
      case "last-month":
        return "lastMonth";
      case "last-3-months":
        return "last3Months";
      case "last-6-months":
        return "last6Months";
      default:
        return "";
    }
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setSelectedFilter(value);
    setIsCustomRange(value === "custom");

    if (value !== "custom") {
      fetchSmsLogs(value);
    }
  };

  const handleDateChange = (e) => {
    setCustomDateRange({
      ...customDateRange,
      [e.target.name]: e.target.value,
    });
  };

  const handleCustomDateSubmit = () => {
    if (customDateRange.startDate && customDateRange.endDate) {
      fetchSmsLogs("custom");
    }
  };

  const handleSenderFilterChange = (e) => {
    setSenderFilter(e.target.value);
    fetchSmsLogs(selectedFilter, { sender: e.target.value });
  };

  const handlePurposeFilterChange = (e) => {
    setPurposeFilter(e.target.value);
    fetchSmsLogs(selectedFilter, { purpose: e.target.value });
  };

  const handleProviderFilterChange = (option) => {
    const value = option?.value ?? "";
    setProviderFilter(value);
    fetchSmsLogs(selectedFilter, { provider: value });
  };

  // Fetch SMS logs with filters
  const fetchSmsLogs = async (filterValue = selectedFilter, overrides = {}) => {
    setLoading(true);
    try {
      let url = "/admin/smsLog/count";
      let params = {};

      if (filterValue === "custom") {
        if (customDateRange.startDate && customDateRange.endDate) {
          params = {
            startDate: customDateRange.startDate,
            endDate: customDateRange.endDate,
          };
        }
      } else if (filterValue) {
        params = {
          timeFrame: getApiTimeFrame(filterValue),
        };
      }

      // Add sender, purpose, and provider filters — use overrides for the value that just changed
      const currentSender = overrides.sender !== undefined ? overrides.sender : senderFilter;
      const currentPurpose = overrides.purpose !== undefined ? overrides.purpose : purposeFilter;
      const currentProvider = overrides.provider !== undefined ? overrides.provider : providerFilter;
      if (currentSender) {
        params.senderType = currentSender;
      }
      if (currentPurpose) {
        params.purpose = currentPurpose;
      }
      if (currentProvider) {
        params.provider = currentProvider;
      }

      const response = await axios.get(url, { params });

      if (response.data.success) {
        setSmsLogs(response.data.data);
        setSummary({
          totalAlreadyPaidCount: response.data.totalAlreadyPaidCount || 0,
          totalChargedCount: response.data.totalChargedCount || 0,
          grandTotal: response.data.grandTotal || 0,
          senderBreakdown: response.data.senderBreakdown || {
            alpha: { alreadyPaid: 0, charged: 0, total: 0 },
            other: { alreadyPaid: 0, charged: 0, total: 0 },
          },
          purposeBreakdown: response.data.purposeBreakdown || {},
        });
        setAvailablePurposes(response.data.availablePurposes || []);
        setDateRangeLabel(response.data.dateRangeLabel || "All Time");
      } else {
        setError("Failed to fetch data.");
      }
    } catch (err) {
      setError(err.message || "An error occurred while fetching data.");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSenderFilter("");
    setPurposeFilter("");
    setProviderFilter("");
    setSelectedFilter("");
    setIsCustomRange(false);
    setCustomDateRange({ startDate: "", endDate: "" });
    fetchSmsLogs("");
  };

  useEffect(() => {
    fetchSmsLogs();
  }, []);

  const formatPurposeName = (purpose) => {
    if (!purpose) return "Unknown";
    // Convert camelCase to readable format
    return purpose
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  return (
    <div className="container mx-auto p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-darkergray mb-6 flex items-center gap-2">
          SMS Logs Analytics
        </h1>

        {/* Filters Section */}
        <div className="bg-white border border-mediumgray rounded-lg p-4 mb-6">
          <h2 className="text-lg font-semibold text-darkgray mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div>
              <label
                htmlFor="dateFilter"
                className="block text-sm font-medium text-darkergray mb-1"
              >
                Date Range
              </label>
              <select
                id="dateFilter"
                className="w-full border border-mediumgray rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-darkgray focus:border-darkgray"
                value={selectedFilter}
                onChange={handleFilterChange}
              >
                <option value="">All Time</option>
                <option value="today">Today</option>
                <option value="last-week">Last 7 Days</option>
                <option value="last-month">Last 30 Days</option>
                <option value="last-3-months">Last 3 Months</option>
                <option value="last-6-months">Last 6 Months</option>
                <option value="custom">Custom Date Range</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="senderFilter"
                className="block text-sm font-medium text-darkergray mb-1"
              >
                Sender Type
              </label>
              <select
                id="senderFilter"
                className="w-full border border-mediumgray rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-darkgray focus:border-darkgray"
                value={senderFilter}
                onChange={handleSenderFilterChange}
              >
                <option value="">All Senders</option>
                <option value="alpha">Alpha Senders</option>
                <option value="other">Other Senders</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="purposeFilter"
                className="block text-sm font-medium text-darkergray mb-1"
              >
                Purpose
              </label>
              <select
                id="purposeFilter"
                className="w-full border border-mediumgray rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-darkgray focus:border-darkgray"
                value={purposeFilter}
                onChange={handlePurposeFilterChange}
              >
                <option value="">All Purposes</option>
                {availablePurposes.map((purpose) => (
                  <option key={purpose} value={purpose}>
                    {formatPurposeName(purpose)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-darkergray mb-1">
                Provider
              </label>
              <SearchSelect
                options={PROVIDER_FILTER_OPTIONS}
                value={PROVIDER_FILTER_OPTIONS.find((o) => o.value === providerFilter) ?? PROVIDER_FILTER_OPTIONS[0]}
                onChange={handleProviderFilterChange}
                searchable={false}
              />
            </div>

            {isCustomRange && (
              <>
                <div>
                  <label
                    htmlFor="startDate"
                    className="block text-sm font-medium text-darkergray mb-1"
                  >
                    Start Date
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    className="w-full border border-mediumgray rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-darkgray focus:border-darkgray"
                    value={customDateRange.startDate}
                    onChange={handleDateChange}
                  />
                </div>
                <div className="flex flex-col">
                  <label
                    htmlFor="endDate"
                    className="block text-sm font-medium text-darkergray mb-1"
                  >
                    End Date
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      id="endDate"
                      name="endDate"
                      className="flex-1 border border-mediumgray rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-darkgray focus:border-darkgray"
                      value={customDateRange.endDate}
                      onChange={handleDateChange}
                    />
                    <Button
                      className="gray-button"
                      onClick={handleCustomDateSubmit}
                      disabled={
                        !customDateRange.startDate || !customDateRange.endDate
                      }
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </>
            )}

            <div className="flex items-end">
              <Button className="black-button py-2.5" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-lightgray border-t-darkergray"></div>
            <p className="mt-2 text-darkgray">Loading...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white border border-mediumgray rounded-lg p-6">
                <h3 className="text-lg font-semibold text-darkgray mb-2">
                  Already Paid SMS
                </h3>
                <div className="text-3xl font-bold text-darkergray">
                  {summary.totalAlreadyPaidCount}
                </div>
                <p className="text-sm text-darkgray">SMS already paid for</p>
              </div>
              <div className="bg-white border border-mediumgray rounded-lg p-6">
                <h3 className="text-lg font-semibold text-darkgray mb-2">
                  Charged SMS
                </h3>
                <div className="text-3xl font-bold text-darkergray">
                  {summary.totalChargedCount}
                </div>
                <p className="text-sm text-darkgray">SMS charged to client</p>
              </div>
              <div className="bg-white border border-mediumgray rounded-lg p-6">
                <h3 className="text-lg font-semibold text-darkgray mb-2">
                  Total SMS
                </h3>
                <div className="text-3xl font-bold text-darkergray">
                  {summary.grandTotal}
                </div>
                <p className="text-sm text-darkgray">All SMS sent</p>
              </div>
            </div>

            {/* Sender Type Breakdown Table */}
            <div className="bg-white border border-mediumgray rounded-lg">
              <div className="p-4 border-b border-mediumgray">
                <h3 className="text-lg font-semibold text-darkgray">
                  Sender Type Breakdown - {dateRangeLabel}
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-lightgray">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-darkergray border-b">
                        Sender Type
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-darkergray border-b">
                        Already Paid
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-darkergray border-b">
                        Charged
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-darkergray border-b">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-lightgray">
                      <td className="px-4 py-3 text-sm font-medium text-darkergray">
                        Alpha Senders
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-darkgray">
                        {summary.senderBreakdown.alpha.alreadyPaid}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-darkgray">
                        {summary.senderBreakdown.alpha.charged}
                      </td>
                      <td className="px-4 py-3 text-center text-sm font-medium text-darkergray">
                        {summary.senderBreakdown.alpha.total}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-medium text-darkergray">
                        Other Senders
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-darkgray">
                        {summary.senderBreakdown.other.alreadyPaid}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-darkgray">
                        {summary.senderBreakdown.other.charged}
                      </td>
                      <td className="px-4 py-3 text-center text-sm font-medium text-darkergray">
                        {summary.senderBreakdown.other.total}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Purpose Breakdown Table */}
            {Object.keys(summary.purposeBreakdown).length > 0 && (
              <div className="bg-white border border-mediumgray rounded-lg">
                <div className="p-4 border-b border-mediumgray">
                  <h3 className="text-lg font-semibold text-darkgray">
                    Purpose Breakdown
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-lightgray">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-darkergray border-b">
                          Purpose
                        </th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-darkergray border-b">
                          Already Paid
                        </th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-darkergray border-b">
                          Charged
                        </th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-darkergray border-b">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(summary.purposeBreakdown)
                        .sort(([, a], [, b]) => b.total - a.total)
                        .map(([purpose, counts], index) => (
                          <tr
                            key={purpose}
                            className={
                              index <
                              Object.keys(summary.purposeBreakdown).length - 1
                                ? "border-b border-lightgray"
                                : ""
                            }
                          >
                            <td className="px-4 py-3 text-sm font-medium text-darkergray">
                              {formatPurposeName(purpose)}
                            </td>
                            <td className="px-4 py-3 text-center text-sm text-darkgray">
                              {counts.alreadyPaid}
                            </td>
                            <td className="px-4 py-3 text-center text-sm text-darkgray">
                              {counts.charged}
                            </td>
                            <td className="px-4 py-3 text-center text-sm font-medium text-darkergray">
                              {counts.total}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Office Details */}
            <div className="bg-white border border-mediumgray rounded-lg">
              <div className="p-4 border-b border-mediumgray">
                <h3 className="text-lg font-semibold text-darkgray">
                  Office-wise Details
                </h3>
              </div>

              {smsLogs.length === 0 ? (
                <div className="p-8 text-center text-mediumgray">
                  No SMS logs found for the selected filters.
                </div>
              ) : (
                <div className="p-4">
                  {smsLogs.map((log, index) => (
                    <Disclosure key={index} className="mb-4 last:mb-0">
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="flex justify-between items-center w-full py-3 px-4 text-left bg-lightgray hover:bg-mediumgray rounded-lg border border-mediumgray transition-colors my-3">
                            <div className="flex-1">
                              <h4 className="font-semibold text-darkergray">
                                {log.officeName}
                              </h4>
                              <div className="flex gap-6 mt-1 text-sm text-darkgray">
                                <span>
                                  Already Paid: {log.officeAlreadyPaidCount}
                                </span>
                                <span>Charged: {log.officeChargedCount}</span>
                                <span>
                                  Total:{" "}
                                  <span className="font-medium text-darkergray">
                                    {log.officeTotalCount}
                                  </span>
                                </span>
                              </div>
                            </div>
                            <HiChevronDown
                              className={`${
                                open ? "rotate-180 transform" : ""
                              } w-5 h-5 text-darkgray transition-transform`}
                            />
                          </Disclosure.Button>
                          <Disclosure.Panel className="mt-2">
                            {/* Office Purpose Breakdown */}
                            {log.officePurposeBreakdown &&
                              Object.keys(log.officePurposeBreakdown).length >
                                0 && (
                                <div className="mb-4 bg-white border border-mediumgray rounded-lg overflow-hidden">
                                  <div className="p-3 bg-lightgray border-b border-mediumgray">
                                    <h5 className="text-sm font-semibold text-darkergray">
                                      Office Purpose Breakdown
                                    </h5>
                                  </div>
                                  <table className="w-full">
                                    <thead className="bg-mediumgray">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-darkergray">
                                          Purpose
                                        </th>
                                        <th className="px-4 py-2 text-center text-xs font-medium text-darkergray">
                                          Already Paid
                                        </th>
                                        <th className="px-4 py-2 text-center text-xs font-medium text-darkergray">
                                          Charged
                                        </th>
                                        <th className="px-4 py-2 text-center text-xs font-medium text-darkergray">
                                          Total
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {Object.entries(log.officePurposeBreakdown)
                                        .sort(([, a], [, b]) => b.total - a.total)
                                        .map(([purpose, counts], idx) => (
                                          <tr
                                            key={purpose}
                                            className={
                                              idx <
                                              Object.keys(
                                                log.officePurposeBreakdown
                                              ).length -
                                                1
                                                ? "border-b border-lightgray"
                                                : ""
                                            }
                                          >
                                            <td className="px-4 py-2 text-xs text-darkergray">
                                              {formatPurposeName(purpose)}
                                            </td>
                                            <td className="px-4 py-2 text-center text-xs text-darkgray">
                                              {counts.alreadyPaid}
                                            </td>
                                            <td className="px-4 py-2 text-center text-xs text-darkgray">
                                              {counts.charged}
                                            </td>
                                            <td className="px-4 py-2 text-center text-xs font-medium text-darkergray">
                                              {counts.total}
                                            </td>
                                          </tr>
                                        ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}

                            {/* Users Table */}
                            <div className="bg-lightgray rounded-lg border border-mediumgray overflow-hidden">
                              <table className="w-full">
                                <thead className="bg-mediumgray">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-darkergray">
                                      User
                                    </th>
                                    <th className="px-4 py-3 text-center text-sm font-medium text-darkergray">
                                      Already Paid
                                    </th>
                                    <th className="px-4 py-3 text-center text-sm font-medium text-darkergray">
                                      Charged
                                    </th>
                                    <th className="px-4 py-3 text-center text-sm font-medium text-darkergray">
                                      Total
                                    </th>
                                    <th className="px-4 py-3 text-center text-sm font-medium text-darkergray">
                                      Alpha
                                    </th>
                                    <th className="px-4 py-3 text-center text-sm font-medium text-darkergray">
                                      Other
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white">
                                  {log.users.map((user, userIndex) => (
                                    <React.Fragment key={userIndex}>
                                      <tr
                                        className={
                                          userIndex < log.users.length - 1
                                            ? "border-b border-lightgray"
                                            : ""
                                        }
                                      >
                                        <td className="px-4 py-3 text-sm font-medium text-darkergray">
                                          {user.name || "Unknown User"}
                                        </td>
                                        <td className="px-4 py-3 text-center text-sm text-darkgray">
                                          {user.alreadyPaidCount}
                                        </td>
                                        <td className="px-4 py-3 text-center text-sm text-darkgray">
                                          {user.chargedCount}
                                        </td>
                                        <td className="px-4 py-3 text-center text-sm font-medium text-darkergray">
                                          {user.totalCount}
                                        </td>
                                        <td className="px-4 py-3 text-center text-sm text-darkgray">
                                          {user.senderBreakdown?.alpha || 0}
                                        </td>
                                        <td className="px-4 py-3 text-center text-sm text-darkgray">
                                          {user.senderBreakdown?.other || 0}
                                        </td>
                                      </tr>
                                      {/* User Purpose Breakdown */}
                                      {user.purposeBreakdown &&
                                        Object.keys(user.purposeBreakdown)
                                          .length > 0 && (
                                          <tr>
                                            <td
                                              colSpan="6"
                                              className="px-4 py-2 bg-gray-50"
                                            >
                                              <details className="text-xs">
                                                <summary className="cursor-pointer text-darkgray font-medium hover:text-darkergray">
                                                  View Purpose Breakdown
                                                </summary>
                                                <table className="w-full mt-2 text-xs">
                                                  <thead className="bg-gray-100">
                                                    <tr>
                                                      <th className="px-2 py-1 text-left text-darkergray">
                                                        Purpose
                                                      </th>
                                                      <th className="px-2 py-1 text-center text-darkergray">
                                                        Already Paid
                                                      </th>
                                                      <th className="px-2 py-1 text-center text-darkergray">
                                                        Charged
                                                      </th>
                                                      <th className="px-2 py-1 text-center text-darkergray">
                                                        Total
                                                      </th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {Object.entries(
                                                      user.purposeBreakdown
                                                    )
                                                      .sort(
                                                        ([, a], [, b]) =>
                                                          b.total - a.total
                                                      )
                                                      .map(
                                                        ([purpose, counts]) => (
                                                          <tr
                                                            key={purpose}
                                                            className="border-t border-gray-200"
                                                          >
                                                            <td className="px-2 py-1 text-darkergray">
                                                              {formatPurposeName(
                                                                purpose
                                                              )}
                                                            </td>
                                                            <td className="px-2 py-1 text-center text-darkgray">
                                                              {
                                                                counts.alreadyPaid
                                                              }
                                                            </td>
                                                            <td className="px-2 py-1 text-center text-darkgray">
                                                              {counts.charged}
                                                            </td>
                                                            <td className="px-2 py-1 text-center font-medium text-darkergray">
                                                              {counts.total}
                                                            </td>
                                                          </tr>
                                                        )
                                                      )}
                                                  </tbody>
                                                </table>
                                              </details>
                                            </td>
                                          </tr>
                                        )}
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SmsLogs;